import { useState, useEffect, useMemo, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MemberCard from './MemberCard';
import ProfileModal from './ProfileModal';
import { getSuperteamMembers, verifyMember, unverifyMember, getSuperteamLeaderboard } from '../utils/api';
import { useClickSound } from '../hooks/useClickSound';
import type { Superteam, Member, User } from '../types';

interface LocationPanelProps {
  superteam: Superteam | null;
  superteams: Superteam[];
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (superteam: Superteam) => void;
  user: User | null;
  isAuthenticated: boolean;
  onJoin: (superteamId: string) => Promise<void>;
  onLeave: () => Promise<void>;
  joinLoading: boolean;
}

// No mock members - real members come from the database
const MOCK_MEMBERS: Record<string, Member[]> = {};

export default function LocationPanel({ 
  superteam,
  superteams,
  isOpen, 
  onClose,
  onNavigate,
  user,
  isAuthenticated,
  onJoin,
  onLeave,
  joinLoading,
}: LocationPanelProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isTwitterExpanded, setIsTwitterExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { withClickSound } = useClickSound();

  // Check if user is a member of this superteam
  const isMember = user?.superteamId === superteam?.id;

  // Navigation - find previous and next superteams
  const currentIndex = useMemo(() => {
    if (!superteam) return -1;
    return superteams.findIndex(s => s.id === superteam.id);
  }, [superteam, superteams]);

  const prevSuperteam = useMemo(() => {
    if (currentIndex <= 0) return superteams[superteams.length - 1]; // Wrap to end
    return superteams[currentIndex - 1];
  }, [currentIndex, superteams]);

  const nextSuperteam = useMemo(() => {
    if (currentIndex >= superteams.length - 1) return superteams[0]; // Wrap to start
    return superteams[currentIndex + 1];
  }, [currentIndex, superteams]);

  const handlePrev = useCallback(() => {
    if (prevSuperteam) onNavigate(prevSuperteam);
  }, [prevSuperteam, onNavigate]);

  const handleNext = useCallback(() => {
    if (nextSuperteam) onNavigate(nextSuperteam);
  }, [nextSuperteam, onNavigate]);
  
  // Check if user can verify members (lead or lieutenant in same superteam)
  const canVerify = isMember && (user?.role === 'lead' || user?.role === 'lieutenant');

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Refresh members when user joins/leaves
  useEffect(() => {
    if (superteam && isOpen) {
      queryClient.invalidateQueries({ queryKey: ['members', superteam.id] });
    }
  }, [user?.superteamId, superteam, isOpen, queryClient]);

  // Reset twitter expanded state when superteam changes
  useEffect(() => {
    setIsTwitterExpanded(false);
  }, [superteam?.id]);

  const handleJoinClick = async () => {
    if (superteam) {
      await onJoin(superteam.id);
    }
  };

  const handleLeaveClick = async () => {
    await onLeave();
  };

  const handleVerify = useCallback(async (memberId: string) => {
    try {
      setVerifyLoading(memberId);
      await verifyMember(memberId);
      // Refresh the members list
      queryClient.invalidateQueries({ queryKey: ['members', superteam?.id] });
    } catch (error) {
      console.error('Failed to verify member:', error);
    } finally {
      setVerifyLoading(null);
    }
  }, [queryClient, superteam?.id]);

  const handleUnverify = useCallback(async (memberId: string) => {
    try {
      setVerifyLoading(memberId);
      await unverifyMember(memberId);
      // Refresh the members list
      queryClient.invalidateQueries({ queryKey: ['members', superteam?.id] });
    } catch (error) {
      console.error('Failed to unverify member:', error);
    } finally {
      setVerifyLoading(null);
    }
  }, [queryClient, superteam?.id]);

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members', superteam?.id],
    queryFn: () => superteam ? getSuperteamMembers(superteam.id) : Promise.resolve({ members: [], totalCount: 0 }),
    enabled: !!superteam && isOpen,
  });

  // Fetch leaderboard to get dynamic ranking
  const { data: leaderboardData } = useQuery({
    queryKey: ['leaderboard', 'superteams'],
    queryFn: getSuperteamLeaderboard,
    staleTime: 60000, // 1 minute
    enabled: isOpen,
  });

  // Get rank for current superteam
  const superteamRank = useMemo(() => {
    if (!superteam || !leaderboardData?.data?.leaderboard) return null;
    const entry = leaderboardData.data.leaderboard.find(e => e.id === superteam.id);
    return entry?.rank || null;
  }, [superteam, leaderboardData]);

  const members = useMemo(() => {
    if (membersData?.members?.length) return membersData.members;
    if (superteam?.id && MOCK_MEMBERS[superteam.id]) return MOCK_MEMBERS[superteam.id];
    return [];
  }, [membersData, superteam?.id]);

  if (!superteam) return null;

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: isMobile ? 250 : 250,
    transition: 'opacity 0.3s',
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: 251,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    ...(isMobile ? {
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: '20px 20px 0 0',
      maxHeight: '85vh',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
    } : {
      top: '70px',
      right: '16px',
      bottom: '70px',
      width: '380px',
      borderRadius: '20px',
      transform: isOpen ? 'translateX(0)' : 'translateX(calc(100% + 32px))',
    }),
  };

  const headerStyle: CSSProperties = {
    flexShrink: 0,
    padding: '20px',
    position: 'relative',
  };

  const statCardStyle: CSSProperties = {
    flex: 1,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: '12px',
    padding: '12px',
    textAlign: 'center',
  };

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div style={panelStyle}>
        {/* Navigation Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <button
            onClick={withClickSound(handlePrev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
            }}
          >
            <svg style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {prevSuperteam?.name.replace('Superteam ', '')}
            </span>
          </button>

          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            {currentIndex + 1} / {superteams.length}
          </span>

          <button
            onClick={withClickSound(handleNext)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: 'rgba(255,255,255,0.7)',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
            }}
          >
            <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nextSuperteam?.name.replace('Superteam ', '')}
            </span>
            <svg style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>

        {/* Header */}
        <div style={headerStyle}>
          {isMobile && (
            <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 16px' }} />
          )}
          
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {superteam.iconUrl ? (
                <img 
                  src={superteam.iconUrl} 
                  alt={superteam.name}
                  style={{ width: '56px', height: '56px', borderRadius: '14px', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)' }}
                />
              ) : (
                <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'linear-gradient(135deg, #14F195, #9945FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                  🌐
                </div>
              )}
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>{superteam.name}</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>{superteam.city}, {superteam.country}</p>
              </div>
            </div>
            
            <button
              onClick={withClickSound(onClose)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <div style={statCardStyle}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'white' }}>{superteam.memberCount}</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Members</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#14F195' }}>{superteam.activeProjects}</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Projects</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: '#9945FF' }}>#{superteamRank || '—'}</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Rank</div>
            </div>
          </div>
        </div>
        
        {/* Divider */}
        <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(20,241,149,0.3), transparent)', margin: '0 20px' }} />

        {/* Social Links - Collapsible */}
        {superteam.twitter && (
          <div style={{ flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {/* Collapsible Header */}
            <button
              onClick={withClickSound(() => setIsTwitterExpanded(!isTwitterExpanded))}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.6)' }} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>Social & Updates</span>
              </div>
              <svg 
                style={{ 
                  width: '16px', 
                  height: '16px', 
                  color: 'rgba(255,255,255,0.4)',
                  transform: isTwitterExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }} 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
            
            {/* Collapsible Content */}
            <div style={{
              maxHeight: isTwitterExpanded ? '200px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
            }}>
              <div style={{ padding: '0 20px 16px' }}>
                <a
                  href={`https://x.com/${superteam.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(29, 155, 240, 0.2), rgba(29, 155, 240, 0.1))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg style={{ width: '20px', height: '20px', color: '#1d9bf0' }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>@{superteam.twitter}</div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Follow for updates & announcements</div>
                  </div>
                  <svg style={{ width: '16px', height: '16px', color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Members List */}
        <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>Team Members</h3>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)', padding: '4px 8px', borderRadius: '6px' }}>
              {members.length} showing
            </span>
          </div>

          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 0' }}>
              <div className="animate-spin" style={{ width: '32px', height: '32px', border: '2px solid rgba(20,241,149,0.3)', borderTopColor: '#14F195', borderRadius: '50%' }} />
            </div>
          ) : members.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {members.map((member) => (
                <MemberCard 
                  key={member.id} 
                  member={member}
                  canVerify={canVerify}
                  onVerify={handleVerify}
                  onUnverify={handleUnverify}
                  verifyLoading={verifyLoading === member.id}
                  onClick={setSelectedMember}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }}>
                👥
              </div>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: 500 }}>No members yet</p>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '4px' }}>Be the first to join!</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: '20px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {isMember ? (
            // User is already a member - show Leave button
            <button 
              onClick={handleLeaveClick}
              disabled={joinLoading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 600,
                fontSize: '14px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                cursor: joinLoading ? 'not-allowed' : 'pointer',
                opacity: joinLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {joinLoading ? (
                <div style={{ width: '18px', height: '18px', border: '2px solid rgba(239,68,68,0.3)', borderTopColor: '#ef4444', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : null}
              Leave {superteam.name}
            </button>
          ) : isAuthenticated ? (
            // User is logged in but not a member - show Join button
            <button 
              onClick={handleJoinClick}
              disabled={joinLoading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 600,
                fontSize: '14px',
                background: 'linear-gradient(to right, #14F195, #0da672)',
                color: 'black',
                border: 'none',
                cursor: joinLoading ? 'not-allowed' : 'pointer',
                opacity: joinLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {joinLoading ? (
                <div style={{ width: '18px', height: '18px', border: '2px solid rgba(0,0,0,0.3)', borderTopColor: 'black', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : null}
              Join {superteam.name}
            </button>
          ) : (
            // User is not logged in - show Sign In button
            <button 
              onClick={handleJoinClick}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 600,
                fontSize: '14px',
                background: 'linear-gradient(to right, #14F195, #0da672)',
                color: 'black',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Sign in to Join
            </button>
          )}
        </div>
      </div>

      {/* Profile Modal */}
      {selectedMember && (
        <ProfileModal 
          member={selectedMember} 
          onClose={() => setSelectedMember(null)} 
        />
      )}
    </>
  );
}
